import React, { Component, Fragment } from 'react';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Button, Paper, IconButton, MenuItem, Menu } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/MoreHoriz';
import { Formik, Form } from 'formik';
import _last from 'lodash/last';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import Grid from '@material-ui/core/Grid';
import { withApollo } from 'react-apollo';
import { AppContext } from '../../AppProvider';
import * as Queries from '../../graphQL/queries/shipmentQueries';
import * as Mutations from '../../graphQL/mutations/shipmentMutations';
import combineStyles from '../../utils/combineStyles';
import TypographyStyles from '../../styles/typography';
import LayoutStyles from '../../styles/layout';
import SpacingStyles from '../../styles/helpers/spacing';
import ButtonStyles from '../../styles/button';
import ShipmentFormStyles from './ShipmentForm.styles';
import ShipmentFormBasicInfo from '../../components/shipment/form/BasicInfo';
import ErrorNotification from '../../components/common/ErrorNotification';
import LoadingIndicator from '../../components/LoadingIndicator';
import ShipmentReferenceForm from '../../components/shipment/form/References';
import ShipmentFormRates from '../../components/shipment/form/Rates';
import ShipmentFormLocation from '../../components/shipment/form/Location';
import ShipmentFormDocuments from '../../components/shipment/form/Documents';
import ShipmentFormServices from '../../components/shipment/form/Services';
import ShipmentFormBOLInstr from '../../components/shipment/form/BOLInstructions';
import ShipmentFormEquipment from '../../components/shipment/form/Equipment';
import ShipmentContents from '../../components/shipment/form/ShipmentContents/ShipmentContents';
import ConfirmDialogModal from '../../components/Global/Modals/ConfirmDialog';
import { Notifications, EmptyNotification } from './ShipmentDetails.mock';
import getShipmentModel from '../../Models/Shipment';
import * as helpers from './helpers';
import ShipmentFormBillingInfo from '../../components/shipment/form/BillingInfo';
import {
  sanitizeShipment,
  removeUntouchedFields,
  showEquipment,
  rateIsntLCP,
  pickRequiredToRateFields
} from '../../utils/Shipment';
import {
  FORMACTIONS,
  SHIPMENT_TYPE,
  BILL_TO,
  SHIPMENT_STATUS,
  SHIPMENT_DIRECTION,
  ERROR_SEVERITY
} from '../../constants/constants';
import NotificationSnackbar from './../../components/common/NotificationSnackbar';
import ShipmentFormSchema from './ShipmentForm.schema';
import ErrorValidationModal from '../../components/shipment/Modals/ErrorValidation';
import { validateShipment, difference } from './../../utils/Validation';
import { all as customFieldQuery } from '../../graphQL/queries/customFields';
import { parseErrors } from '../../utils/queryHelpers';
import { ApolloError } from 'apollo-boost';
import RateInvalidModal from './../../components/shipment/Modals/RateInvalid';
export class ShipmentForm extends Component {
  state = {
    loading: true,
    error: null,
    shipment: null,
    snackbarOpen: false,
    snackbarMsg: '',
    snackbarType: '',
    savingShipment: false,
    referenceFields: {
      locations: [],
      contacts: []
    },
    openNotificationFormModal: false,
    openConfirmDeleteNotificationModal: false,
    openCancelModal: false,
    openValidationModal: false,
    openDeleteModal: false,
    openRateInvalidModal: false,
    errorList: {},
    notifications: [...Notifications],
    notification: {
      ...EmptyNotification
    },
    validationSchema: null,
    defaultLocation: null,
    defaultBolInstructions: null,
    defaultBillingTerms: null,
    fromQuickRate: false,
    isOpenFileUploader: false,
    carrierSupportsAutoTender: false,
    rateSelected: false,
    actionMenuElement: null
  };

  async componentDidMount() {
    const {
      match: {
        params: { id }
      },
      history: { location = {} },
      client: ApolloClient,
      t
    } = this.props;
    const { orgInfo, currentOrgId } = this.context.globalProps;

    const { state } = location;
    let fromQuickRate = false;
    if (state) {
      fromQuickRate = true;
    }
    const validationSchema = ShipmentFormSchema({ t });
    try {
      let shipment = _get(state, 'shipment') || getShipmentModel();

      const locationDropdowns = await helpers.getLocationContactDropdowns(
        ApolloClient,
        currentOrgId
      );

      const defaultBolInstructions = _get(
        orgInfo,
        'config.defaultBolInstructions',
        null
      );

      const referenceFields = {
        contacts: _get(locationDropdowns, 'contacts', []),
        locations: _get(locationDropdowns, 'locations', [])
      };
      const defaultLocationId = _get(orgInfo, 'config.defaultLocationId', null);
      const defaultBillingTerms = _get(
        orgInfo,
        'config.defaultBillingTerms',
        null
      );
      const defaultLocation = defaultLocationId
        ? helpers.getLocationById(defaultLocationId, referenceFields.locations)
        : null;

      if (id) {
        const {
          data: {
            shipment: { byId: shipmentData }
          }
        } = await ApolloClient.query({
          query: Queries.shipmentById,
          variables: { id },
          fetchPolicy: 'network-only',
          skip: true
        });
        shipment = shipmentData;
      } else {
        // new shipment, default billing info
        shipment.billToInfo = this._getBillTo(
          shipment.shipmentDirectionId,
          defaultBillingTerms
        );
      }
      if (shipment.shipmentStatusId === SHIPMENT_STATUS.CANCELLED) {
        this.props.history.push(`/shipments/${id}`);
      }
      const shipmentReferences = await this._getReferences();
      const shipmentReferenceFields = helpers.populateReferenceFields(
        shipmentReferences,
        shipment.references
      );
      shipment.handlingUnits.map(unit => {
        unit.stackable = !unit.doNotStack;
        return unit;
      });
      shipment.references = shipmentReferenceFields.formValues;
      shipment.pickup =
        _get(shipment, 'pickup', null) || shipment.stops[0] || {};
      shipment.delivery =
        _get(shipment, 'delivery', null) || _last(shipment.stops) || {};
      shipment.isNew = !shipment.id;
      shipment.isDraft =
        shipment.shipmentStatusId === SHIPMENT_STATUS.SAVED ? true : false;

      const carrierSupportsAutoTender = _get(
        shipment,
        'selectedShipmentRate.carrier.supportsAutoTender',
        false
      );
      const rateSelected =
        _get(shipment, 'selectedShipmentRate.rateListGuid', null) !== null;
      if (!shipment.isNew) {
        shipment.ratedShipmentState = pickRequiredToRateFields(shipment);
      }
      

      this.setState({
        referenceFields,
        shipment,
        shipmentReferences,
        validationSchema,
        defaultLocation,
        defaultBolInstructions,
        defaultBillingTerms,
        fromQuickRate,
        shipmentReferenceFields,
        carrierSupportsAutoTender,
        rateSelected
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  }

  _getBillTo = (shipmentDirection, defaultBillingTerms) => {
    const defaultBillTo = _get(defaultBillingTerms, 'defaultPaymentTerms');

    if (defaultBillTo === BILL_TO.THIRD_PARTY) {
      const billToInfo = {
        billTo: BILL_TO.THIRD_PARTY,
        companyName: defaultBillingTerms.companyName,
        billToAddress: defaultBillingTerms.addressInfo,
        billToContactInfo: defaultBillingTerms.contactInfo
      };
      return billToInfo;
    } else {
      let billTo = BILL_TO.SHIPPER;
      switch (shipmentDirection) {
        case SHIPMENT_DIRECTION.INBOUND:
          billTo = BILL_TO.CONSIGNEE;
          break;
        case SHIPMENT_DIRECTION.DROP:
          billTo = BILL_TO.THIRD_PARTY;
          break;
        default:
          //shipper
          break;
      }
      const billToInfo = {
        billTo: billTo,
        companyName: defaultBillingTerms.companyName,
        billToAddress: defaultBillingTerms.addressInfo,
        billToContactInfo: defaultBillingTerms.contactInfo
      };
      return billToInfo;
    }
  };

  _getReferences = async () => {
    const { client: ApolloClient } = this.props;
    const { currentOrgId } = this.context.globalProps;
    try {
      const {
        data: {
          customField: { all }
        }
      } = await ApolloClient.query({
        query: customFieldQuery,
        variables: { orgId: currentOrgId, includeExpired: false }
      });
      return Promise.resolve(all);
    } catch (error) {
      this.setState({
        error
      });
    }
    return '';
  };
  _handleOnUpdateBillingInfo = (setFieldValue, billingInfo) => {
    const {
      billToContactInfo,
      billToAddress,
      billTo,
      companyName
    } = billingInfo;

    setFieldValue('billToInfo.billTo', billTo);

    if (billTo === BILL_TO.THIRD_PARTY) {
      setFieldValue(
        'billToInfo.billToContactInfo.firstName',
        billToContactInfo.firstName
      );
      setFieldValue(
        'billToInfo.billToContactInfo.lastName',
        billToContactInfo.lastName
      );
      setFieldValue(
        'billToInfo.billToContactInfo.email',
        billToContactInfo.email
      );
      setFieldValue(
        'billToInfo.billToContactInfo.phone',
        billToContactInfo.phone
      );

      setFieldValue(
        'billToInfo.billToAddress.address1',
        billToAddress.address1
      );
      setFieldValue(
        'billToInfo.billToAddress.address2',
        billToAddress.address2
      );
      setFieldValue('billToInfo.billToAddress.city', billToAddress.city);
      setFieldValue('billToInfo.billToAddress.state', billToAddress.state);
      setFieldValue(
        'billToInfo.billToAddress.postalCode',
        billToAddress.postalCode
      );
      setFieldValue('billToInfo.companyName', companyName);
      setFieldValue('billToInfo.billToAddress.country', billToAddress.country);
    }
  };
  _handleOnTagDocument = () => {};

  _handleOnDeleteDocument = () => {};

  _handleOpenNotificationFormModal = notificationId => {
    const { notifications } = this.state;
    let notification = notifications.find(
      notification => notification.id === notificationId
    );

    if (!notification) {
      notification = {
        ...EmptyNotification
      };
    }

    this.setState({ notification }, () => {
      this.setState({ openNotificationFormModal: true });
    });
  };

  _handleCloseNotificationFormModal = () => {
    this.setState({ openNotificationFormModal: false });
  };
  _handleCloseErrorValidation = () => {
    this.setState({ openValidationModal: false }, () => {
      window.scrollTo(0, 0);
    });
  };
  _handleSaveNotificationRecipient = notificationRecipient => {
    let { notifications } = this.state;
    if (notificationRecipient.id) {
      notifications = notifications.map(notification => {
        if (notification.id === notificationRecipient.id) {
          return notificationRecipient;
        }
        return notification;
      });
    } else {
      notifications.push({
        ...notificationRecipient,
        id: notifications.length + 1
      });
    }

    this.setState(
      {
        notifications: [...notifications]
      },
      () => {
        this._handleCloseNotificationFormModal();
      }
    );
  };

  _handleRemoveNotificationRecipient = notificationRecipientId => {
    const { notifications } = this.state;
    this.setState(
      {
        notifications: [
          ...notifications.filter(
            notification => notification.id !== notificationRecipientId
          )
        ]
      },
      () => {
        this._handleCloseConfirmDeleteNotificationModal();
        this._handleCloseNotificationFormModal();
      }
    );
  };

  _handleOpenConfirmDeleteNotificationModal = () => {
    this.setState({ openConfirmDeleteNotificationModal: true });
  };
  _handleOpenRateInvalidModal = () => {
    this.setState({ openRateInvalidModal: true });
  };
  _handleCloseRateInvalidModal = () => {
    this.setState({ openRateInvalidModal: false });
  };
  _isRateInvalid = shipmentForm => {
    const currentRatedShipmentState = pickRequiredToRateFields(shipmentForm);
    if (currentRatedShipmentState.totalLinearFeet === '') {
      currentRatedShipmentState.totalLinearFeet = null;
    }
    const lastRatedShipmentState = shipmentForm.ratedShipmentState;
    if (lastRatedShipmentState.totalLinearFeet === '') {
      lastRatedShipmentState.totalLinearFeet = null;
    }
    const changedFieldsObject = difference(
      currentRatedShipmentState,
      shipmentForm.ratedShipmentState
    );

    return !_isEmpty(changedFieldsObject) ? true : false;
  };
  _handleCloseConfirmDeleteNotificationModal = () => {
    this.setState({ openConfirmDeleteNotificationModal: false });
  };
  _handleFormSubmit = async (action, values, touched, setErrors) => {
    const { t, history } = this.props;
    const { orgInfo } = this.context.globalProps;
    let shipmentForm = values;

    shipmentForm.totalLinearFeet = parseInt(shipmentForm.totalLinearFeet) || undefined;
    //I clear out errors before each form submit in case they change there  minds from tender to draft.
    setErrors({});

    // TODO: Why are we only doing this on DRAFT?
    if (action === FORMACTIONS.DRAFT) {
      shipmentForm = removeUntouchedFields(action, values, touched);
    }
    //Tweak a couple of shipment variables (These are used for validation and not persisted in the )
    shipmentForm.isRateSelected =
      _get(shipmentForm, 'selectedShipmentRate.id', null) ||
      shipmentForm.customRateItemGuid
        ? true
        : null;

    shipmentForm.isLCPExceptionRequired =
      shipmentForm.isRateSelected &&
      rateIsntLCP(shipmentForm.selectedShipmentRate, shipmentForm.shipmentRates)
        ? true
        : false;
    // Populate Bill To
    if (
      shipmentForm.billToInfo.billTo &&
      shipmentForm.billToInfo.billTo !== BILL_TO.THIRD_PARTY
    ) {
      const stops = {
        [BILL_TO.SHIPPER]: shipmentForm.pickup,
        [BILL_TO.CONSIGNEE]: shipmentForm.delivery
      };

      shipmentForm.billToInfo = helpers.formatBillToInfo(
        shipmentForm.billToInfo,
        stops[shipmentForm.billToInfo.billTo]
      );
    }

    // Validate
    const validationErrors = await validateShipment(
      action,
      shipmentForm,
      touched
    );

    // Do the Action (Save or Tender)
    if (_isEmpty(validationErrors)) {
      const shipment = sanitizeShipment(action, shipmentForm, orgInfo);

      this.setState({
        savingShipment: true,
        openCancelModal: false
      });
      let shipmentId;
      try {
        switch (action) {
          case FORMACTIONS.DRAFT:
            await this._saveShipment(shipment);
            history.push(`/shipments`);
            break;
          case FORMACTIONS.UPDATE:
            ({ shipmentId } = await this._saveShipment(shipment));
            history.push(`/shipments/${shipmentId}`);
            break;
          case FORMACTIONS.TENDER:
            ({ shipmentId } = await this._tenderShipment(shipment, true));
            history.push(`/shipments/${shipmentId}/success`);
            break;
          case FORMACTIONS.MANUALLY_TENDER:
            ({ shipmentId } = await this._tenderShipment(shipment, false));
            history.push(`/shipments/${shipmentId}/success`);
            break;
          default:
            throw new Error(`FORMACTION "${action}" not recognized`);
        }
      } catch (error) {
        //TODO: Put error to log somewhere useful
        console.log('error', error)
        const errors = error.graphQLErrors.map(e => e.extensions ? e.extensions.data.text : []);

        this.setState({
          snackbarMsg: t('Failed to save'),
          snackbarOpen: true,
          savingShipment: false,
          openValidationModal: true,
          errorList: errors
        });
        return;
      }
    } else {
      this.setState({
        openValidationModal: true,
        errorList: validationErrors
      });
      setErrors(validationErrors);
    }
  };
  _saveShipment = async shipment => {
    const { client: ApolloClient } = this.props;
    const response = await ApolloClient.mutate({
      mutation: Mutations.saveShipmentMutation,
      variables: {
        shipment: { ...shipment }
      }
    });

    const {
      data: {
        shipment: {
          save: { id: shipmentId }
        }
      }
    } = response;

    return { shipmentId };
  };
  // TODO: Duplicated in Status.js
  _tenderShipment = async (shipment, autoTender) => {
    const { client: ApolloClient } = this.props;
    const response = await ApolloClient.mutate({
      mutation: Mutations.tenderShipmentMutation,
      // Set error policy because tender is a 2-step process.
      // The save could success while the tender fails
      errorPolicy: 'all',
      variables: {
        shipment: { ...shipment },
        attemptAutoTender: autoTender
      }
    });

    const { data, errors } = response;

    const errs = parseErrors(errors);
    if (errs.severity >= ERROR_SEVERITY.Error) {
      throw new ApolloError({
        graphQLErrors: errors,
        networkError: null,
        errorMessage: 'GraphQLError...',
        extraInfo: null
      });
    }

    const {
      shipment: {
        tender: { id: shipmentId }
      }
    } = data;

    return { shipmentId, errs };
  };

  _handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false, snackbarMsg: '' });
  };
  _handleCancelClick = () => {
    this.setState({
      openCancelModal: true
    });
  };
  _handleCancelDiscard = () => {
    const { shipment } = this.state;
    const {
      history,
      match: {
        params: { id }
      }
    } = this.props;

    if (shipment.isNew || shipment.shipmentStatusId === SHIPMENT_STATUS.SAVED) {
      history.push('/shipments');
    } else {
      history.push(`/shipments/${id}`);
    }
  };
  _handleDeleteClick = () => {
    this.setState({
      openDeleteModal: true
    });
  };
  _handleDeleteClose = () => {
    this.setState({
      openDeleteModal: false
    });
  };
  _handleDelete = async () => {
    const {
      history,
      match: {
        params: { id }
      },
      client: ApolloClient
    } = this.props;

    // Execute Delete mutation
    try {
      await ApolloClient.mutate({
        mutation: Mutations.deleteShipment,
        variables: {
          shipment: { shipmentId: id, ignoreWarnings: true }
        }
      });
      history.push('/shipments');
      return true;
    } catch (error) {
      // TODO: This is insufficient error handling
      this.setState({ error });
      return false;
    }
  };
  _handleValidateField = async (
    currentField,
    currentValue,
    values,
    setFieldError
  ) => {
    const { validationSchema } = this.state;
    const schema = validationSchema;
    const valueCopy = { ...values };
    const updatedValues = _set(valueCopy, currentField, currentValue);
    try {
      await schema.validateAt(currentField, updatedValues);
      setFieldError(currentField, null);
    } catch (err) {
      setFieldError(currentField, err.message);
    }
  };

  _handleCancelOnClose = () => {
    this.setState({
      openCancelModal: false
    });
  };

  _handleAddFile = () => this.setState({ isOpenFileUploader: true });

  _handleCloseFileUploader = () => this.setState({ isOpenFileUploader: false });

  _handleSelectRate = ({ carrierSupportsAutoTender }) => {
    this.setState({
      carrierSupportsAutoTender,
      rateSelected: true
    });
  };

  _handleActionMenuClick = event => {
    if (this.state.actionMenuElement) {
      this._handleActionMenuClose();
      return;
    }

    this.setState({
      actionMenuElement: event.currentTarget
    });
  };
  _handleActionMenuClose = () => {
    this.setState({ actionMenuElement: null });
  };

  render() {
    const { classes, t } = this.props;
    const {
      orgInfo,
      orgRefs: { glCodes }
    } = this.context.globalProps;

    const {
      loading,
      error,
      referenceFields,
      shipment,
      openValidationModal,
      errorList,
      validationSchema,
      defaultLocation,
      defaultBolInstructions,
      defaultBillingTerms,
      fromQuickRate,
      shipmentReferenceFields,
      carrierSupportsAutoTender,
      rateSelected,
      actionMenuElement,
      openRateInvalidModal
    } = this.state;

    // TODO: Add check for org autotender
    const canAutoTender =
      orgInfo.config.autoTenderEnabled && carrierSupportsAutoTender;
    const readyToTender = rateSelected;

    if (loading) {
      return <LoadingIndicator fixedPosition={true} />;
    }

    if (error) {
      return <ErrorNotification message={t('Error loading page')} />;
    }

    const bolInstructions = shipment.bolInstructions;

    return (
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        initialValues={{
          id: shipment.id,
          ignoreWarnings: false,
          glCodeId: shipment.glCodeId,
          shipmentStatusId: shipment.shipmentStatusId,
          shipmentModeId: shipment.shipmentModeId,
          shipmentDirectionId: shipment.shipmentDirectionId,
          proNumber: shipment.proNumber,
          bolNumber: shipment.bolNumber,
          carrierPickupNumber: shipment.carrierPickupNumber,
          totalLinearFeet: shipment.totalLinearFeet,
          requestedServices: shipment.requestedServices,
          requestedEquipment: shipment.requestedEquipment,
          bolInstructions: bolInstructions ? bolInstructions : '',
          pickup: shipment.pickup,
          billToInfo: shipment.billToInfo,
          delivery: shipment.delivery,
          rateListGuid: null,
          rateItemGuid: null,
          references: shipment.references,
          handlingUnits: shipment.handlingUnits,
          selectedShipmentRate: shipment.selectedShipmentRate || {},
          lcpExceptionReasonId: _get(
            shipment,
            'selectedShipmentRate.lcpExceptionReasonId',
            null
          ),
          lcpExceptionReasonNotes: _get(
            shipment,
            'selectedShipmentRate.lcpExceptionReasonNotes',
            null
          ),
          shipmentRates: shipment.shipmentRates,
          ratedShipmentState: shipment.ratedShipmentState || {}, // this freezes fields in the form to validate if we invalidate a rate
          rowVersion: String(shipment.rowVersion) //this is needed for validation
        }}
        render={({
          values,
          _errors,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldError,
          setErrors,
          touched
        }) => (
          <Fragment>
            <Form onSubmit={handleSubmit} className={classes.pageRoot}>
              <div className={classes.titleWrapper}>
                <h1
                  className={classnames(classes.headline4, classes.pageTitle)}
                >
                  {shipment.isNew
                    ? `${t('Create Shipment')}`
                    : `${t('Edit Shipment')} #${values.id}`}
                </h1>
              </div>
              <ShipmentFormBasicInfo
                handleChange={handleChange}
                handleValidateField={(currentField, currentValue) => {
                  this._handleValidateField(
                    currentField,
                    currentValue,
                    values,
                    setFieldError
                  );
                }}
                proNumber={values.proNumber}
                bolNumber={values.bolNumber}
                carrierPickupNumber={values.carrierPickupNumber}
                shipmentStatusId={values.shipmentStatusId}
                shipmentModeId={values.shipmentModeId}
                plannedToTimeLocal={values.pickup.plannedToTimeLocal}
                shipmentDirectionId={values.shipmentDirectionId}
                totalLinearFeet={values.totalLinearFeet}
                setFieldValue={setFieldValue}
                glCodeId={values.glCodeId}
                glCodes={glCodes}
              />
              <div className={classes.body}>
                <div className={classes.sidebar}>
                  {!shipment.isNew && (
                    <ShipmentFormDocuments
                      values={{ id: values.id, files: shipment.shipmentFiles }}
                      shipment={shipment}
                      editable={true}
                    />
                  )}
                  <ShipmentFormBillingInfo
                    billToInfo={values.billToInfo}
                    isNewShipment={shipment.isNew}
                    setFieldValue={setFieldValue}
                    onUpdate={this._handleOnUpdateBillingInfo}
                    contacts={referenceFields.contacts}
                    locations={referenceFields.locations}
                    shipmentDirection={values.shipmentDirectionId}
                    defaultBillingTerms={defaultBillingTerms}
                    editable={true}
                  />
                </div>
                <div className={classes.content}>
                  <ShipmentFormLocation
                    values={values.pickup}
                    touched={touched}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    contacts={referenceFields.contacts}
                    locations={referenceFields.locations}
                    locationType="pickup"
                    defaultLocation={defaultLocation}
                    defaultDockOpen={orgInfo.config.defaultDockOpenTime}
                    defaultDockClose={orgInfo.config.defaultDockCloseTime}
                    isNewShipment={shipment.isNew}
                    fromQuickRate={fromQuickRate}
                    shipmentDirection={values.shipmentDirectionId}
                    label={t('Pickup')}
                    handleValidateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                  <ShipmentFormLocation
                    values={values.delivery}
                    touched={touched}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    contacts={referenceFields.contacts}
                    locations={referenceFields.locations}
                    locationType="delivery"
                    isNewShipment={shipment.isNew}
                    fromQuickRate={fromQuickRate}
                    shipmentDirection={values.shipmentDirectionId}
                    label={t('Delivery')}
                    defaultLocation={defaultLocation}
                    defaultDockOpen={orgInfo.config.defaultDockOpenTime}
                    defaultDockClose={orgInfo.config.defaultDockCloseTime}
                    handleValidateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                  <ShipmentContents
                    handlingUnits={values.handlingUnits}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    contacts={referenceFields.contacts}
                    handleValidateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                  {values.shipmentModeId !== SHIPMENT_TYPE.PARCEL && (
                    <Grid
                      container
                      spacing={16}
                      classes={{ container: classes.servicesEquipment }}
                    >
                      <Grid
                        item
                        lg={
                          values.shipmentModeId === SHIPMENT_TYPE.LTL ? 12 : 6
                        }
                      >
                        <ShipmentFormServices
                          values={values}
                          handleChange={handleChange}
                        />
                      </Grid>
                      {showEquipment(values.shipmentModeId) && (
                        <Grid item lg={6}>
                          <ShipmentFormEquipment
                            handleChange={handleChange}
                            values={values}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                  <ShipmentReferenceForm
                    values={values.references}
                    referenceFields={shipmentReferenceFields}
                    onChange={handleChange}
                    handleValidateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                  <ShipmentFormRates
                    values={values}
                    handleSelectRate={this._handleSelectRate}
                    handleChange={handleChange}
                    shipmentRates={values.shipmentRates}
                    selectedShipmentRate={values.selectedShipmentRate}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    setErrors={setErrors}
                    fromQuickRate={fromQuickRate}
                    handleValidateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                  <ShipmentFormBOLInstr
                    values={values}
                    handleChange={handleChange}
                    defaultBolInstructions={defaultBolInstructions}
                  />
                  <div className={classes.actionsPlaceholder} />
                  <Paper square elevation={5} className={classes.actions}>
                    {shipment.isNew || shipment.isDraft ? (
                      // New or Draft Shipment
                      <Fragment>
                        <Button
                          variant={readyToTender ? 'outlined' : 'contained'}
                          color="primary"
                          className={classnames(
                            classes.button,
                            classes.actionDraft
                          )}
                          classes={{ label: classes.buttonLabel }}
                          onClick={() => {
                            this._handleFormSubmit(
                              FORMACTIONS.DRAFT,
                              values,
                              touched,
                              setErrors
                            );
                          }}
                        >
                          {t('Save as draft')}
                        </Button>
                        {readyToTender && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              if (this._isRateInvalid(values)) {
                                this._handleOpenRateInvalidModal();
                              } else {
                                this._handleFormSubmit(
                                  canAutoTender
                                    ? FORMACTIONS.TENDER
                                    : FORMACTIONS.MANUALLY_TENDER,
                                  values,
                                  touched,
                                  setErrors
                                );
                              }
                            }}
                            className={classnames(
                              classes.button,
                              classes.actionSave
                            )}
                            classes={{ label: classes.buttonLabel }}
                          >
                            {canAutoTender
                              ? t('Tender Shipment')
                              : t('Manually Tender')}
                          </Button>
                        )}
                        <IconButton
                          color="primary"
                          onClick={this._handleActionMenuClick}
                          classes={{
                            root: classnames(
                              classes.noPadding,
                              classes.statusMenu
                            )
                          }}
                        >
                          <MenuIcon />
                        </IconButton>
                      </Fragment>
                    ) : (
                      // Editing Shipment Details
                      <Fragment>
                        <Button
                          className={classes.button}
                          onClick={() => {
                            this._handleCancelClick();
                          }}
                          color={'primary'}
                          classes={{ label: classes.buttonLabel }}
                        >
                          {t('Discard Changes')}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            this._handleFormSubmit(
                              FORMACTIONS.UPDATE,
                              values,
                              touched,
                              setErrors
                            );
                          }}
                          className={classnames(
                            classes.button,
                            classes.actionSave
                          )}
                          classes={{ label: classes.buttonLabel }}
                        >
                          {t('Save Shipment')}
                        </Button>
                      </Fragment>
                    )}
                  </Paper>
                </div>
              </div>
              <RateInvalidModal
                open={openRateInvalidModal}
                onClose={this._handleCloseRateInvalidModal}
                onConfirm={() => {
                  this._handleCloseRateInvalidModal();
                  const shipmentForm = { ...values };
                  setFieldValue('ignoreWarnings', true); // This needs to do in case the shipment fails validation
                  shipmentForm.ignoreWarnings = true;

                  this._handleFormSubmit(
                    FORMACTIONS.TENDER,
                    shipmentForm,
                    touched,
                    setErrors
                  );
                }}
              />
            </Form>

            <Menu
              anchorEl={actionMenuElement}
              open={Boolean(actionMenuElement)}
              onClose={this._handleActionMenuClose}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              style={{
                zIndex: 991
              }}
            >
              {canAutoTender && (
                <MenuItem
                  onClick={() => {
                    this._handleFormSubmit(
                      FORMACTIONS.MANUALLY_TENDER,
                      values,
                      touched,
                      setErrors
                    );
                  }}
                >
                  {t('Manually Tender')}
                </MenuItem>
              )}
              {shipment.isDraft && (
                <MenuItem
                  onClick={() => {
                    this._handleDeleteClick();
                  }}
                >
                  {t('Delete Draft')}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  this._handleCancelClick();
                }}
              >
                {t('Discard Changes')}
              </MenuItem>
            </Menu>

            <ErrorValidationModal
              open={openValidationModal}
              errors={errorList}
              onClose={this._handleCloseErrorValidation}
            />
            <ConfirmDialogModal
              open={this.state.openConfirmDeleteNotificationModal}
              title={t('Are you sure you want to remove this recipient?')}
              message={`${this.state.notification.user} ${t(
                'will no longer receive any notifications for this shipment.'
              )}`}
              confirmLabel={t('Delete')}
              onClose={this._handleCloseConfirmDeleteNotificationModal}
              onConfirm={() =>
                this._handleRemoveNotificationRecipient(
                  this.state.notification.id
                )
              }
            />
            <ConfirmDialogModal
              open={this.state.openCancelModal}
              title={t('Are you sure you want to discard your changes?')}
              confirmLabel={
                shipment.isNew || shipment.isDraft ? t('Save Draft') : t('Save')
              }
              cancelLabel={t('Discard')}
              onClose={this._handleCancelOnClose}
              onCancel={this._handleCancelDiscard}
              onConfirm={() => {
                this._handleFormSubmit(
                  shipment.isNew || shipment.isDraft
                    ? FORMACTIONS.DRAFT
                    : FORMACTIONS.SAVED,
                  values,
                  touched,
                  setErrors
                );
              }}
            />
            <ConfirmDialogModal
              open={this.state.openDeleteModal}
              title={t('Are you sure you want to delete this shipment?')}
              confirmLabel={t('Yes')}
              cancelLabel={t('No')}
              onClose={this._handleDeleteClose}
              onCancel={this._handleDeleteClose}
              onConfirm={this._handleDelete}
            />
            <NotificationSnackbar
              open={this.state.snackbarOpen}
              handleSnackbarClose={this._handleSnackbarClose}
              message={this.state.snackbarMsg}
            />
            {this.state.savingShipment && (
              <LoadingIndicator fixedPosition={true} />
            )}
          </Fragment>
        )}
      />
    );
  }
}

ShipmentForm.contextType = AppContext;
const ShipmentFormWithTranslations = withNamespaces()(withApollo(ShipmentForm));
export default withStyles(
  combineStyles(
    TypographyStyles,
    LayoutStyles,
    SpacingStyles,
    ButtonStyles,
    ShipmentFormStyles
  )
)(ShipmentFormWithTranslations);
